// ─────────────────────────────────────────────────────────────────────
// Access Management ▸ Direct User Permissions tab — Round 6 (2026).
//
// Embedded version of the standalone /admin/user-permissions page. Reuses the
// SAME backend endpoints (UserPermissionsController):
//   GET    /api/admin/user-permissions/catalog
//   GET    /api/admin/user-permissions/employees
//   GET    /api/admin/user-permissions/:empId           (effective, by source)
//   POST   /api/admin/user-permissions/:empId           (grant direct)
//   DELETE /api/admin/user-permissions/:empId/:perm      (revoke direct)
//
// Round 1 added grant/revoke audit on these endpoints. Only DIRECT-source
// grants are revocable here; Role/Department grants are read-only (change the
// role or department to alter those). The standalone page remains available
// and untouched.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardHeader, CardBody, Input, Button, Spinner, Badge, Table,
    Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchPermissionCatalog, fetchPermissionEmployees,
    fetchUserPermissionSnapshot, grantUserPermission, revokeUserPermission,
    denyUserPermission, restoreUserPermission,
} from "../../../api/accessManagement";
import useAuth from "../../Personal Attendance/useAuth";

// Access-Management hardening (Round 1) — the tab mirrors the server-side
// delegation rules (AccessDelegationGuard); the server is the boundary:
//   • you cannot change your own permissions (R-B);
//   • protected permissions (catalog isProtected) and IT-Admin targets can
//     only be changed by an IT Admin (R-A);
//   • a non-IT-Admin may only grant permissions they hold (R-C);
//   • an employee who is not Active cannot be granted/denied (R-D).
const isActiveStatus = (s) => String(s ?? "").trim().toUpperCase() === "A";

const SOURCE_COLORS = { Direct: "primary", Role: "secondary", Department: "info", Denied: "danger", Other: "secondary" };

export default function DirectPermissionsTab() {
    const [catalog, setCatalog]     = useState([]);
    const [employees, setEmployees] = useState([]);
    const [bootLoading, setBootLoading] = useState(true);
    const [bootError, setBootError]     = useState(null);

    const [search, setSearch]               = useState("");
    const [selectedEmpId, setSelectedEmpId] = useState(null);
    const [snapshot, setSnapshot]           = useState(null);
    const [snapLoading, setSnapLoading]     = useState(false);
    const [snapError, setSnapError]         = useState(null);

    const [working, setWorking]     = useState(false);
    const [grantOpen, setGrantOpen] = useState(false);
    const [pendingPerm, setPendingPerm] = useState("");
    const [grantNote, setGrantNote]     = useState("");   // Round 6: optional audit note
    const [grantFilter, setGrantFilter] = useState("");   // Round 6: filter the picker

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setBootLoading(true); setBootError(null);
            try {
                const [perms, emps] = await Promise.all([
                    fetchPermissionCatalog(),
                    fetchPermissionEmployees(),
                ]);
                if (cancelled) return;
                setCatalog(perms);
                setEmployees(emps);
            } catch {
                if (!cancelled) setBootError("Could not load the permission catalog or employee list.");
            } finally {
                if (!cancelled) setBootLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!selectedEmpId) { setSnapshot(null); return; }
        let cancelled = false;
        (async () => {
            setSnapLoading(true); setSnapError(null);
            try {
                const snap = await fetchUserPermissionSnapshot(selectedEmpId);
                if (cancelled) return;
                if (!snap) setSnapError("Employee not found.");
                else setSnapshot(snap);
            } catch {
                if (!cancelled) setSnapError("Could not load this user's permissions.");
            } finally {
                if (!cancelled) setSnapLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedEmpId]);

    const filteredEmployees = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) =>
            (e.empId || "").toLowerCase().includes(q) ||
            (e.name || "").toLowerCase().includes(q) ||
            (e.department || "").toLowerCase().includes(q) ||
            (e.role || "").toLowerCase().includes(q)
        );
    }, [employees, search]);

    const auth = useAuth();
    const targetIsSelf = !!snapshot && String(snapshot.employee?.empId ?? "").toLowerCase() === String(auth.empId ?? "").toLowerCase();
    const targetIsItAdmin = !!snapshot?.employee?.isItAdmin;
    const targetInactive = !!snapshot && !isActiveStatus(snapshot.employee?.empStatus);
    // Everything is locked for your own row, and for an IT Admin's row unless you are one.
    const targetLocked = targetIsSelf || (targetIsItAdmin && !auth.isItAdmin);
    const targetLockReason = targetIsSelf
        ? "You cannot change your own permissions — ask another IT Admin."
        : "Only an IT Admin can change the permissions of an IT Admin.";
    const protectedSet = useMemo(
        () => new Set(catalog.filter((p) => p.isProtected).map((p) => p.permissionName.toLowerCase())),
        [catalog]
    );
    const isProtectedPerm = (name) => protectedSet.has(String(name || "").toLowerCase());
    // A single permission action is locked when the target is locked, or the
    // permission is protected and the caller is not an IT Admin.
    const permLocked = (name) => targetLocked || (isProtectedPerm(name) && !auth.isItAdmin);
    const permLockReason = (name) => targetLocked ? targetLockReason : "Protected permission — only an IT Admin can change it.";

    const availableToGrant = useMemo(() => {
        if (!snapshot) return [];
        const have = new Set((snapshot.permissions || []).map((p) => p.permissionName.toLowerCase()));
        return catalog
            .filter((p) => !have.has(p.permissionName.toLowerCase()))
            // R-A / R-C: a non-IT-Admin can offer neither protected permissions
            // nor permissions they do not hold themselves.
            .filter((p) => auth.isItAdmin || (!p.isProtected && auth.hasAccess(p.permissionName)));
        
    }, [snapshot, catalog, auth.isItAdmin]);

    const openGrant = () => {
        setPendingPerm(availableToGrant[0]?.permissionName || "");
        setGrantOpen(true);
    };

    const onGrant = async () => {
        if (!pendingPerm) return;
        setWorking(true);
        try {
            const fresh = await grantUserPermission(selectedEmpId, pendingPerm, grantNote.trim());
            setGrantNote(""); setGrantFilter("");
            setSnapshot(fresh);
            setGrantOpen(false);
            toast.success(`Granted "${pendingPerm}".`);
        } catch (e) {
            toast.error(e?.response?.data?.error || (e?.response?.status === 403 ? "You are not allowed to make that change." : "Could not grant — please retry."));
        } finally {
            setWorking(false);
        }
    };

    const [revokeTarget, setRevokeTarget] = useState(null);
    const onRevoke = async () => {
        if (!revokeTarget) return;
        setWorking(true);
        try {
            const fresh = await revokeUserPermission(selectedEmpId, revokeTarget);
            setSnapshot(fresh);
            toast.success(`Revoked "${revokeTarget}".`);
            setRevokeTarget(null);
        } catch (e) {
            toast.error(e?.response?.data?.error || (e?.response?.status === 403 ? "You are not allowed to make that change." : "Could not revoke — please retry."));
        } finally {
            setWorking(false);
        }
    };

    // DENY removes a role/dept default for this one person.
    const [denyTarget, setDenyTarget] = useState(null);
    const onDeny = async () => {
        if (!denyTarget) return;
        setWorking(true);
        try {
            const fresh = await denyUserPermission(selectedEmpId, denyTarget);
            setSnapshot(fresh);
            toast.success(`Denied "${denyTarget}" for this user.`);
            setDenyTarget(null);
        } catch (e) {
            toast.error(e?.response?.data?.error || (e?.response?.status === 403 ? "You are not allowed to make that change." : "Could not deny — please retry."));
        } finally {
            setWorking(false);
        }
    };

    // RESTORE removes a deny marker (the user's normal grant applies again).
    const onRestore = async (permissionName) => {
        setWorking(true);
        try {
            const fresh = await restoreUserPermission(selectedEmpId, permissionName);
            setSnapshot(fresh);
            toast.success(`Restored "${permissionName}".`);
        } catch (e) {
            toast.error(e?.response?.data?.error || (e?.response?.status === 403 ? "You are not allowed to make that change." : "Could not restore — please retry."));
        } finally {
            setWorking(false);
        }
    };

    if (bootLoading) {
        return <div className="text-center text-muted py-5"><Spinner size="sm" /> Loading…</div>;
    }
    if (bootError) {
        return <div className="alert alert-danger">{bootError}</div>;
    }

    return (
        <>
            <Row className="g-3">
                {/* LEFT: employee picker */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100">
                        <CardHeader className="bg-white border-0 pt-3 px-3 pb-2">
                            <h6 className="fw-semibold mb-2">Employees</h6>
                            <Input
                                type="search"
                                bsSize="sm"
                                placeholder="Search by name, empId, dept, role…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </CardHeader>
                        <CardBody className="p-2" style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
                            {filteredEmployees.length === 0 ? (
                                <div className="text-muted small text-center py-4">No matches.</div>
                            ) : (
                                <ul className="list-unstyled mb-0">
                                    {filteredEmployees.map((e) => (
                                        <li key={e.empId}>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedEmpId(e.empId)}
                                                className="w-100 text-start border-0 rounded p-2 mb-1"
                                                style={{
                                                    background: e.empId === selectedEmpId ? "rgba(91, 80, 214, 0.10)" : "transparent",
                                                    color:      e.empId === selectedEmpId ? "#2c2076" : "#212529",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <div className="fw-medium" style={{ fontSize: 13 }}>{e.name || e.empId}</div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                    {e.empId}
                                                    {e.role ? ` · ${e.role}` : ""}
                                                    {e.department ? ` · ${e.department}` : ""}
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                {/* RIGHT: snapshot */}
                <Col lg={8}>
                    <Card className="border-0 shadow-sm h-100">
                        {!selectedEmpId ? (
                            <CardBody className="text-center text-muted py-5">
                                <i className="ri-arrow-left-line" style={{ fontSize: 32, opacity: 0.4 }} />
                                <div className="mt-2">Pick an employee on the left.</div>
                            </CardBody>
                        ) : snapLoading ? (
                            <CardBody className="text-center text-muted py-5"><Spinner size="sm" /> Loading…</CardBody>
                        ) : snapError ? (
                            <CardBody><div className="alert alert-danger mb-0">{snapError}</div></CardBody>
                        ) : snapshot ? (
                            <>
                                <CardHeader className="bg-white border-0 pt-3 px-3 pb-2 d-flex align-items-center gap-2 flex-wrap">
                                    <div className="flex-grow-1">
                                        <h6 className="fw-semibold mb-1">{snapshot.employee.name || snapshot.employee.empId}</h6>
                                        <span className="text-muted small">
                                            {snapshot.employee.empId}
                                            {snapshot.employee.role ? ` · ${snapshot.employee.role}` : ""}
                                            {snapshot.employee.department ? ` · ${snapshot.employee.department}` : ""}
                                            {snapshot.employee.empStatus ? ` · status ${String(snapshot.employee.empStatus).trim()}` : ""}
                                        </span>
                                        {targetLocked && (
                                            <div className="small text-danger mt-1"><i className="ri-lock-2-line me-1" />{targetLockReason}</div>
                                        )}
                                        {!targetLocked && targetInactive && (
                                            <div className="small text-warning mt-1"><i className="ri-error-warning-line me-1" />Not Active — grants and denies are disabled; revoke / restore still work.</div>
                                        )}
                                    </div>
                                    <Button color="primary" size="sm" onClick={openGrant}
                                        disabled={working || availableToGrant.length === 0 || targetLocked || targetInactive}
                                        title={targetLocked ? targetLockReason : (targetInactive ? "Employee is not Active — permissions cannot be granted." : undefined)}>
                                        <i className="ri-add-line align-bottom me-1" />Grant permission
                                    </Button>
                                </CardHeader>
                                <CardBody className="p-3">
                                    {snapshot.permissions.length === 0 ? (
                                        <div className="text-center text-muted py-4 small">No permissions granted to this user yet.</div>
                                    ) : (
                                        <Table responsive borderless className="mb-0 align-middle">
                                            <thead>
                                                <tr style={{ fontSize: 12, color: "#8a93a8", textTransform: "uppercase", letterSpacing: 0.4 }}>
                                                    <th style={{ width: "40%" }}>Permission</th>
                                                    <th style={{ width: "40%" }}>Description</th>
                                                    <th style={{ width: "12%" }}>Source</th>
                                                    <th style={{ width: "8%" }} className="text-end">&nbsp;</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {snapshot.permissions.map((p) => (
                                                    <tr key={p.permissionName} style={{ borderTop: "1px solid #eef0f4" }}>
                                                        <td className="fw-medium">{p.permissionName}</td>
                                                        <td className="text-muted small">{p.description || "—"}</td>
                                                        <td>
                                                            <Badge color={SOURCE_COLORS[p.source] || "secondary"} pill style={{ fontWeight: 500 }}>
                                                                {p.source}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end">
                                                            {p.source === "Direct" ? (
                                                                <Button color="link" size="sm" className="text-danger p-1"
                                                                    disabled={working || permLocked(p.permissionName)}
                                                                    onClick={() => setRevokeTarget(p.permissionName)}
                                                                    title={permLocked(p.permissionName) ? permLockReason(p.permissionName) : "Revoke this direct grant"}>
                                                                    <i className="ri-delete-bin-line" />
                                                                </Button>
                                                            ) : p.source === "Denied" ? (
                                                                <Button color="link" size="sm" className="p-1"
                                                                    disabled={working || permLocked(p.permissionName)}
                                                                    onClick={() => onRestore(p.permissionName)}
                                                                    title={permLocked(p.permissionName) ? permLockReason(p.permissionName) : "Restore — remove the deny so the user's normal access applies again"}>
                                                                    <i className="ri-arrow-go-back-line" /> Restore
                                                                </Button>
                                                            ) : (
                                                                <Button color="link" size="sm" className="text-danger p-1"
                                                                    disabled={working || permLocked(p.permissionName) || targetInactive}
                                                                    onClick={() => setDenyTarget(p.permissionName)}
                                                                    title={permLocked(p.permissionName) ? permLockReason(p.permissionName) : targetInactive ? "Employee is not Active — denies are disabled." : p.source === "Role"
                                                                        ? "Remove this role default for THIS person only (deny)."
                                                                        : "Remove this department default for THIS person only (deny)."}>
                                                                    <i className="ri-forbid-line" /> Deny
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </>
                        ) : null}
                    </Card>
                </Col>
            </Row>

            {/* Grant modal */}
            <Modal isOpen={grantOpen} toggle={() => !working && setGrantOpen(false)}>
                <ModalHeader toggle={() => !working && setGrantOpen(false)}>Grant permission</ModalHeader>
                <ModalBody>
                    <p className="text-muted small mb-3">
                        Pick a permission to grant directly to{" "}
                        <strong>{snapshot?.employee?.name || selectedEmpId}</strong>. This is in addition
                        to whatever their role already provides.
                    </p>
                    {/* Round 6: filter + grouped picker + optional note */}
                    <Input type="search" bsSize="sm" className="mb-2" placeholder="Filter permissions…"
                           value={grantFilter} onChange={(e) => setGrantFilter(e.target.value)} />
                    <Input type="select" value={pendingPerm} onChange={(e) => setPendingPerm(e.target.value)}>
                        <option value="">— Select a permission —</option>
                        {(() => {
                            const q = grantFilter.trim().toLowerCase();
                            const list = availableToGrant.filter((p) => !q ||
                                p.permissionName.toLowerCase().includes(q) ||
                                (p.description || "").toLowerCase().includes(q));
                            const groups = {};
                            for (const p of list) {
                                const dash = p.permissionName.indexOf(" - ");
                                const g = dash > 0 ? p.permissionName.slice(0, dash) : "Other";
                                (groups[g] = groups[g] || []).push(p);
                            }
                            return Object.keys(groups).sort().map((g) => (
                                <optgroup key={g} label={g}>
                                    {groups[g].map((p) => (
                                        <option key={p.permissionName} value={p.permissionName}>
                                            {p.permissionName}{p.isProtected ? " (protected)" : ""}{p.description ? ` — ${p.description}` : ""}
                                        </option>
                                    ))}
                                </optgroup>
                            ));
                        })()}
                    </Input>
                    <Input type="text" className="mt-2" placeholder="Reason (optional — recorded in the audit trail)"
                           maxLength={200} value={grantNote} onChange={(e) => setGrantNote(e.target.value)} />
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setGrantOpen(false)} disabled={working}>Cancel</Button>
                    <Button color="primary" onClick={onGrant} disabled={working || !pendingPerm}>
                        {working ? <><Spinner size="sm" /> Saving…</> : "Grant"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Revoke confirm */}
            <Modal isOpen={!!revokeTarget} toggle={() => !working && setRevokeTarget(null)}>
                <ModalHeader toggle={() => !working && setRevokeTarget(null)}>Revoke permission</ModalHeader>
                <ModalBody>
                    Remove <strong>{revokeTarget}</strong> from{" "}
                    <strong>{snapshot?.employee?.name || selectedEmpId}</strong>?
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setRevokeTarget(null)} disabled={working}>Cancel</Button>
                    <Button color="danger" onClick={onRevoke} disabled={working}>
                        {working ? <><Spinner size="sm" /> Revoking…</> : "Revoke"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Deny confirm */}
            <Modal isOpen={!!denyTarget} toggle={() => !working && setDenyTarget(null)}>
                <ModalHeader toggle={() => !working && setDenyTarget(null)}>Deny permission</ModalHeader>
                <ModalBody>
                    <p className="mb-2">
                        Remove <strong>{denyTarget}</strong> from{" "}
                        <strong>{snapshot?.employee?.name || selectedEmpId}</strong> — for this person only?
                    </p>
                    <p className="text-muted small mb-0">
                        This overrides the role/department default: the permission stays on the role,
                        but this individual won't have it (frontend and backend). You can restore it
                        anytime. IT Admins are never affected by deny.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setDenyTarget(null)} disabled={working}>Cancel</Button>
                    <Button color="danger" onClick={onDeny} disabled={working}>
                        {working ? <><Spinner size="sm" /> Denying…</> : "Deny for this user"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
